/**
 * @typedef {object} TransferCacheLineV1
 * @property {string} clientLineId
 * @property {string | null} invStockId
 * @property {string} qtyInput
 * @property {string | null} unitId
 * @property {string | null} toLocationId
 * @property {string | null} stockNoPolicy
 * @property {string} remark
 * @property {string} [mtrId]
 * @property {string} [mtrNo]
 * @property {string} [mtrName]
 * @property {string} [stockNo]
 * @property {string} [fromWarehouseLabel]
 * @property {boolean} [isOtherMaterial]
 * @property {number|string} [availableQty]
 */

/**
 * @typedef {object} TransferCacheSelectedWorkOrderV1
 * @property {string} id
 * @property {string} [workOrderNo]
 * @property {string | null} [workWarehouseId]
 */

/**
 * @typedef {object} TransferCachePayloadV1
 * @property {string | null} workWarehouseId
 * @property {string} transferDate
 * @property {string} issueReason
 * @property {string} remark
 * @property {string} [configMode]
 * @property {TransferCacheSelectedWorkOrderV1 | null} [selectedWorkOrder]
 * @property {TransferCacheLineV1[]} lines
 */

const CONFIG_MODE_BY_TARGET_WAREHOUSE = 'byTargetWarehouse';
const CONFIG_MODE_BY_WORK_ORDER = 'byWorkOrder';

/** UI camel ↔ form-cache wire enum（後端 `by_target_warehouse`｜`by_work_order`） */
const CONFIG_MODE_TO_WIRE = {
  [CONFIG_MODE_BY_TARGET_WAREHOUSE]: 'by_target_warehouse',
  [CONFIG_MODE_BY_WORK_ORDER]: 'by_work_order',
};

/**
 * 接受 camel 或 wire 值；未知／空 → byTargetWarehouse（舊五欄草稿預設）。
 * @param {string|null|undefined} value
 * @returns {'byTargetWarehouse'|'byWorkOrder'}
 */
export function normalizeConfigMode(value) {
  if (value === CONFIG_MODE_BY_WORK_ORDER || value === 'by_work_order') {
    return CONFIG_MODE_BY_WORK_ORDER;
  }
  return CONFIG_MODE_BY_TARGET_WAREHOUSE;
}

function createClientLineId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {array} draftLines
 * @returns {array}
 */
export function ensureClientLineIds(draftLines = []) {
  return (draftLines ?? []).map((line) => {
    if (line?.clientLineId) {
      return line;
    }
    return { ...line, clientLineId: createClientLineId() };
  });
}

/**
 * Shell form → cache payload v1（camel）。
 * 持久化顯示用欄，供其他物料回填（不依賴 candidates）。
 * @param {object} form
 * @returns {TransferCachePayloadV1}
 */
function normalizeSelectedWorkOrderForCache(selectedWorkOrder) {
  const id = selectedWorkOrder?.id;
  if (id == null || id === '') {
    return null;
  }
  const workWarehouseId = selectedWorkOrder?.workWarehouseId;
  return {
    id: String(id),
    workOrderNo: selectedWorkOrder?.workOrderNo == null
      ? ''
      : String(selectedWorkOrder.workOrderNo),
    workWarehouseId: workWarehouseId == null || workWarehouseId === ''
      ? null
      : String(workWarehouseId),
  };
}

export function buildCachePayloadFromForm({
  workWarehouseId = null,
  transferDate = '',
  issueReason = '',
  remark = '',
  configMode = 'byTargetWarehouse',
  selectedWorkOrder = null,
  draftLines = [],
} = {}) {
  const linesWithIds = ensureClientLineIds(draftLines);
  return {
    workWarehouseId: workWarehouseId || null,
    transferDate: transferDate == null ? '' : String(transferDate),
    issueReason: issueReason == null ? '' : String(issueReason),
    remark: remark == null ? '' : String(remark),
    configMode: normalizeConfigMode(configMode),
    selectedWorkOrder: normalizeSelectedWorkOrderForCache(selectedWorkOrder),
    lines: linesWithIds.map((line) => {
      const qty = line?.qty;
      let qtyInput = '';
      if (qty !== '' && qty != null) {
        qtyInput = String(qty);
      }
      const availableRaw = line?.availableQty;
      let availableQty = null;
      if (availableRaw !== '' && availableRaw != null) {
        const parsed = Number(availableRaw);
        availableQty = Number.isFinite(parsed) ? parsed : null;
      }
      return {
        clientLineId: line.clientLineId,
        invStockId: line?.invStockId || null,
        qtyInput,
        unitId: line?.unitId ?? null,
        toLocationId: line?.toLocationId ?? null,
        stockNoPolicy: line?.stockNoPolicy ?? null,
        remark: line?.remark == null ? '' : String(line.remark),
        mtrId: line?.mtrId ? String(line.mtrId) : '',
        mtrNo: line?.mtrNo ? String(line.mtrNo) : '',
        mtrName: line?.mtrName ? String(line.mtrName) : '',
        stockNo: line?.stockNo ? String(line.stockNo) : '',
        fromWarehouseLabel: line?.fromWarehouseLabel
          ? String(line.fromWarehouseLabel)
          : '',
        isOtherMaterial: Boolean(line?.isOtherMaterial),
        availableQty,
      };
    }),
  };
}

/**
 * Cache payload → Shell form fields。
 * @param {TransferCachePayloadV1 | null | undefined} payload
 */
export function restoreFormFromPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  return {
    targetWarehouseId: payload.workWarehouseId || '',
    transferDate: payload.transferDate ?? '',
    issueReason: payload.issueReason ?? '',
    remark: payload.remark ?? '',
    configMode: normalizeConfigMode(payload.configMode),
    selectedWorkOrder: normalizeSelectedWorkOrderForCache(payload.selectedWorkOrder),
    draftLines: (payload.lines ?? []).map((line) => {
      const qtyInput = line?.qtyInput ?? '';
      let qty = '';
      if (qtyInput !== '') {
        const parsed = Number(qtyInput);
        qty = Number.isFinite(parsed) ? parsed : qtyInput;
      }
      const availableRaw = line?.availableQty;
      let availableQty = 0;
      if (availableRaw !== '' && availableRaw != null) {
        const parsed = Number(availableRaw);
        availableQty = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      }
      return {
        clientLineId: line?.clientLineId || createClientLineId(),
        invStockId: line?.invStockId || '',
        qty,
        unitId: line?.unitId ?? null,
        toLocationId: line?.toLocationId ?? null,
        stockNoPolicy: line?.stockNoPolicy || 'TRANSFERRED_KEEP_OLD',
        remark: line?.remark || null,
        mtrId: line?.mtrId ? String(line.mtrId) : '',
        mtrNo: line?.mtrNo ? String(line.mtrNo) : '',
        mtrName: line?.mtrName ? String(line.mtrName) : '',
        stockNo: line?.stockNo ? String(line.stockNo) : '',
        availableQty,
        fromWarehouseLabel: line?.fromWarehouseLabel
          ? String(line.fromWarehouseLabel)
          : '',
        isOtherMaterial: Boolean(line?.isOtherMaterial),
      };
    }),
  };
}

function enrichFieldsChanged(prev, next) {
  return prev.availableQty !== next.availableQty
    || prev.mtrId !== next.mtrId
    || prev.mtrNo !== next.mtrNo
    || prev.mtrName !== next.mtrName
    || prev.stockNo !== next.stockNo
    || prev.fromWarehouseLabel !== next.fromWarehouseLabel
    || Boolean(prev.isOtherMaterial) !== Boolean(next.isOtherMaterial);
}

/**
 * candidates 就緒後對齊 draft：invStockId 對不到可轉庫 → 刪除；對到則 enrich。
 * 需求內／其他物料／candidates 空皆同規則（對不到就刪）。
 *
 * @param {array} draftLines
 * @param {array} materials
 * @returns {{ lines: array, removedCount: number, changed: boolean }}
 */
export function reconcileDraftLinesWithCandidates(draftLines = [], materials = []) {
  const demandIds = new Set(
    (materials ?? []).map((material) => material?.mtrId).filter(Boolean),
  );
  const stockById = new Map();
  (materials ?? []).forEach((material) => {
    (material?.transferableInventoryStocks ?? []).forEach((stock) => {
      if (stock?.id) {
        stockById.set(stock.id, { stock, material });
      }
    });
  });

  const lines = [];
  let removedCount = 0;
  let enrichChanged = false;

  (draftLines ?? []).forEach((line) => {
    const invStockId = line?.invStockId;
    if (!invStockId) {
      removedCount += 1;
      return;
    }
    const hit = stockById.get(invStockId);
    if (!hit) {
      removedCount += 1;
      return;
    }
    const { stock, material } = hit;
    const mtrId = material?.mtrId || line.mtrId || '';
    const next = {
      ...line,
      mtrId,
      mtrNo: material?.mtrNo || line.mtrNo || '',
      mtrName: material?.mtrName || line.mtrName || '',
      stockNo: stock?.stockNo || line.stockNo || '',
      availableQty: stock?.availableQty ?? line.availableQty,
      fromWarehouseLabel: [stock?.warehouseLabel, stock?.locationLabel]
        .filter(Boolean)
        .join(' / ') || line.fromWarehouseLabel || '',
      isOtherMaterial: mtrId ? !demandIds.has(mtrId) : true,
    };
    if (enrichFieldsChanged(line, next)) {
      enrichChanged = true;
    }
    lines.push(next);
  });

  return {
    lines,
    removedCount,
    changed: removedCount > 0 || enrichChanged,
  };
}

/** @deprecated 請改用 reconcileDraftLinesWithCandidates */
export function markInvalidLinesAfterCandidates(draftLines = [], materials = []) {
  return reconcileDraftLinesWithCandidates(draftLines, materials).lines;
}

/**
 * 是否為「有填值」的暫存（預設轉倉日alone 不算）。
 * @param {TransferCachePayloadV1 | null | undefined} payload
 */
export function isMeaningfulCachePayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  if (payload.workWarehouseId) {
    return true;
  }
  if (payload.selectedWorkOrder?.id) {
    return true;
  }
  if (normalizeConfigMode(payload.configMode) !== CONFIG_MODE_BY_TARGET_WAREHOUSE) {
    return true;
  }
  if (String(payload.issueReason ?? '').trim()) {
    return true;
  }
  if (String(payload.remark ?? '').trim()) {
    return true;
  }
  if (Array.isArray(payload.lines) && payload.lines.length > 0) {
    return true;
  }
  return false;
}

/**
 * @param {object} snapshot — Shell getFormSnapshot 形狀
 */
export function isMeaningfulFormSnapshot(snapshot) {
  return isMeaningfulCachePayload(buildCachePayloadFromForm({
    workWarehouseId: snapshot?.targetWarehouseId,
    transferDate: snapshot?.transferDate,
    issueReason: snapshot?.issueReason,
    remark: snapshot?.remark,
    configMode: snapshot?.configMode,
    selectedWorkOrder: snapshot?.selectedWorkOrder,
    draftLines: snapshot?.draftLines,
  }));
}

/**
 * 穩定序列化供 exact payload 比對（正式 create 與 snapshot 對應）。
 * @param {TransferCachePayloadV1} payload
 */
export function stableStringifyCachePayload(payload) {
  return JSON.stringify(payload ?? null);
}

/**
 * Local payload → form-cache wire（camel；save／startNew 再 keysToSnake）。
 * 後端 v1 七欄：既有五欄 ＋ config_mode ＋ selected_work_order（摘要僅 id／work_order_no／work_warehouse_id）。
 * by_target_warehouse 時 selected_work_order 一律 null。
 * @param {TransferCachePayloadV1 | null | undefined} payload
 */
export function toServerFormCachePayload(payload) {
  if (payload == null || typeof payload !== 'object') {
    return payload;
  }
  const configMode = normalizeConfigMode(payload.configMode);
  return {
    ...payload,
    configMode: CONFIG_MODE_TO_WIRE[configMode],
    selectedWorkOrder: configMode === CONFIG_MODE_BY_WORK_ORDER
      ? normalizeSelectedWorkOrderForCache(payload.selectedWorkOrder)
      : null,
  };
}

/**
 * Server entry payload（keysToCamel 後）→ local payload 形狀：config_mode 值轉回 camel。
 * 舊五欄草稿缺欄 → byTargetWarehouse／null。
 * @param {object | null | undefined} serverPayload
 * @returns {TransferCachePayloadV1 | null | undefined}
 */
export function fromServerFormCachePayload(serverPayload) {
  if (serverPayload == null || typeof serverPayload !== 'object') {
    return serverPayload;
  }
  const configMode = normalizeConfigMode(serverPayload.configMode);
  return {
    ...serverPayload,
    configMode,
    selectedWorkOrder: configMode === CONFIG_MODE_BY_WORK_ORDER
      ? normalizeSelectedWorkOrderForCache(serverPayload.selectedWorkOrder)
      : null,
  };
}

/**
 * start-new 回應併回本機 payload：以本機送出的兩欄為準（server 已持久化同值）。
 * @param {object | null | undefined} serverPayload
 * @param {TransferCachePayloadV1 | null | undefined} localPayload
 * @returns {TransferCachePayloadV1 | null | undefined}
 */
export function withLocalOnlyCacheFields(serverPayload, localPayload) {
  const normalized = fromServerFormCachePayload(serverPayload);
  if (normalized == null || typeof normalized !== 'object' || !localPayload) {
    return normalized;
  }
  const configMode = normalizeConfigMode(localPayload.configMode);
  return {
    ...normalized,
    configMode,
    selectedWorkOrder: configMode === CONFIG_MODE_BY_WORK_ORDER
      ? normalizeSelectedWorkOrderForCache(localPayload.selectedWorkOrder)
      : null,
  };
}

export { createClientLineId };
